import { useState } from "react";
import { api, type CarouselItem, type EpidemicStatus, type EpidemicTrack } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StudioOptionCard } from "@/components/DesignReviewPanel";
import EpidemicMusicPicker from "@/components/EpidemicMusicPicker";
import { CtaIo } from "@/components/studioTabs";

type Props = {
  item: CarouselItem;
  music?: EpidemicStatus;
  ffmpeg?: boolean;
  tracks: EpidemicTrack[];
  term?: string;
  loading?: boolean;
  searchPending?: boolean;
  composePending?: boolean;
  onSearch: (q: string) => void;
  onCompose: (trackId: string) => void;
};

export default function CarouselReelPanel({
  item,
  music,
  ffmpeg = true,
  tracks: _legacyTracks,
  term: _legacyTerm,
  loading: _loading,
  searchPending: _searchPending,
  composePending,
  onSearch: _onSearch,
  onCompose,
}: Props) {
  const [picked, setPicked] = useState(item.reel_track_id || "");
  const [pickedMeta, setPickedMeta] = useState({
    title: item.reel_track_title || "",
  });
  const reelBusy = composePending || item.reel_status === "queued" || item.reel_status === "rendering";
  const canMux = Boolean(picked && music?.can_download !== false && music?.state !== "missing_key");

  return (
    <StudioOptionCard
      title="Rolka na FB"
      hint="FB dostaje rolkę 9:16 z muzyką Epidemic. IG zostaje karuzelą slajdów."
    >
      <p className="text-xs text-muted-foreground">
        FB = rolka. IG = karuzela. Muzyka musi iść w pliku MP4, Graph nie dokleja biblioteki Meta.
      </p>
      {!ffmpeg ? <p className="text-xs text-warning">Na serwerze nie ma ffmpeg. Rolki nie złożę.</p> : null}
      <EpidemicMusicPicker
        music={music}
        selectedId={picked}
        selectedLabel={pickedMeta.title}
        onSelect={(track) => {
          if (!track) {
            setPicked("");
            setPickedMeta({ title: "" });
            return;
          }
          setPicked(track.id);
          setPickedMeta({ title: `${track.title || ""} · ${track.artist || ""}`.trim() });
        }}
      />
      <Button
        size="sm"
        variant="brand"
        disabled={!item.id || !canMux || !ffmpeg || reelBusy || !picked}
        onClick={() => picked && onCompose(picked)}
      >
        {reelBusy ? "Składam rolkę…" : "Złóż rolkę"}
      </Button>
      <CtaIo io="reel" />
      {item.reel_error ? <p className="text-sm text-destructive">{item.reel_error}</p> : null}
      {item.has_reel ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {item.reel_track_title || "Rolka gotowa"}
            {item.epidemic_license_ref ? ` · ${item.epidemic_license_ref}` : ""}
          </p>
          <video
            className="aspect-[9/16] w-40 rounded border border-border bg-muted"
            controls
            src={`${api.carouselReelUrl(item.id)}?v=${item.updated_at || item.reel_status || ""}`}
          />
        </div>
      ) : null}
    </StudioOptionCard>
  );
}
